import React, { useEffect, useMemo, useState } from 'react';
import { Banknote, Download, PackageCheck, Save } from 'lucide-react';
import { CompanyInfo, InventoryItem, ServiceTicket, StockMovement } from '../types';
import { calculateItemWeightedAvgCost, calculateTicketPartsCost, calculateTicketTotal, formatTL, getTicketPaymentSplit, getTicketPaymentSplitForPeriod, getTicketReportDate } from '../utils/storage';
import { exportPrinterMonthlyReportPDF } from '../utils/pdfGenerator';

interface PrinterMonthlyReportSectionProps {
  tickets: ServiceTicket[];
  inventory: InventoryItem[];
  stockMovements: StockMovement[];
  companyInfo: CompanyInfo;
  month: string;
  canEditSettlement: boolean;
  onSaveCompanyInfo?: (company: CompanyInfo) => void;
}

const isPrinterTicket = (ticket: ServiceTicket, companyInfo: CompanyInfo) => {
  const type = ticket.deviceType || '';
  const area = companyInfo.deviceAreaAssignments?.[type];
  if (area) return area === 'printer';
  const lower = type.toLocaleLowerCase('tr-TR');
  return ['yazıcı', 'yazici', 'tarayıcı', 'tarayici', 'printer', 'scanner', 'fotokopi'].some(word => lower.includes(word));
};

const isPrinterInventoryItem = (item: InventoryItem) => {
  const text = `${item.name} ${item.category} ${item.brand || ''} ${item.model || ''} ${item.code || ''}`.toLocaleLowerCase('tr-TR');
  return [
    'yazıcı', 'yazici', 'printer', 'scanner', 'tarayıcı', 'tarayici', 'fotokopi',
    'toner', 'kartuş', 'kartus', 'mürekkep', 'murekkep', 'ink', 'drum', 'fuser',
    'printhead', 'baskı kafası', 'baski kafasi', 'imaging', 'transfer belt',
    'transfer kayışı', 'paper feed', 'pickup roller', 'developer', 'bakım kiti', 'bakim kiti',
  ].some(word => text.includes(word));
};

const money = (value: number) => formatTL(Number.isFinite(value) ? value : 0);

export const PrinterMonthlyReportSection: React.FC<PrinterMonthlyReportSectionProps> = ({
  tickets,
  inventory,
  stockMovements,
  companyInfo,
  month,
  canEditSettlement,
  onSaveCompanyInfo,
}) => {
  const savedSettlement = companyInfo.printerMonthlySettlements?.[month] ?? 0;
  const [settlementDraft, setSettlementDraft] = useState(String(savedSettlement));

  useEffect(() => {
    setSettlementDraft(String(savedSettlement));
  }, [month, savedSettlement]);

  const report = useMemo(() => {
    const allPrinterTickets = tickets.filter(ticket =>
      !ticket.isDeleted &&
      isPrinterTicket(ticket, companyInfo) &&
      ticket.serviceType !== 'Sozlesmeli' &&
      !ticket.contractedCompanyId
    );
    const printerTickets = allPrinterTickets.filter(ticket => Boolean(getTicketReportDate(ticket) && getTicketReportDate(ticket).startsWith(month)));
    const printerPaymentTickets = allPrinterTickets.filter(ticket => getTicketPaymentSplitForPeriod(ticket, month).total > 0);

    let guaranteedExVat = 0;
    let nonGuaranteedExVat = 0;
    let outsideServiceRevenue = 0;
    const collectedInMonth = printerPaymentTickets.reduce((sum, ticket) => sum + getTicketPaymentSplitForPeriod(ticket, month).total, 0);
    const toBeCollectedFromMonthForms = printerTickets.reduce((sum, ticket) => {
      const { grandTotal } = calculateTicketTotal(
        ticket.parts || [], ticket.laborCost || 0, ticket.vatRate || 0,
        ticket.discount || 0, ticket.isVatIncluded || false, ticket.roundingAdjustment || 0
      );
      return sum + Math.max(0, grandTotal - getTicketPaymentSplit(ticket).total);
    }, 0);
    let usedPartsNet = 0;
    let outsideServiceExpense = 0;

    printerPaymentTickets.forEach(ticket => {
      const { subtotal, grandTotal } = calculateTicketTotal(
        ticket.parts || [], ticket.laborCost || 0, ticket.vatRate || 0,
        ticket.discount || 0, ticket.isVatIncluded || false, ticket.roundingAdjustment || 0
      );
      const received = getTicketPaymentSplitForPeriod(ticket, month).total;
      const exVatReceived = grandTotal > 0 ? received * (subtotal / grandTotal) : 0;
      if (ticket.serviceType === 'DisServis') {
        outsideServiceRevenue += exVatReceived;
      } else {
        const isGuaranteed = ticket.serviceType === 'Garantili' || ticket.serviceType === 'ServisGarantili' || ticket.isUnderWarranty;
        if (isGuaranteed) guaranteedExVat += exVatReceived;
        else nonGuaranteedExVat += exVatReceived;
      }
    });
    printerTickets.forEach(ticket => {
      usedPartsNet += calculateTicketPartsCost(ticket.parts || []);
    });
    outsideServiceExpense = printerTickets
      .filter(ticket => ticket.serviceType === 'DisServis')
      .reduce((sum, ticket) => sum + (Number(ticket.outsideServiceCost) || 0), 0);

    const allPrinterTicketIds = new Set(allPrinterTickets.map(ticket => ticket.id));
    const printerTicketPartIds = new Set<string>();
    const printerPartNames = new Set<string>();
    const printerPartCodes = new Set<string>();
    allPrinterTickets.forEach(ticket => {
      ticket.parts?.forEach(part => {
        if (part.inventoryItemId) printerTicketPartIds.add(part.inventoryItemId);
        if (part.name?.trim()) printerPartNames.add(part.name.trim().toLocaleLowerCase('tr-TR'));
        if (part.code?.trim()) printerPartCodes.add(part.code.trim().toLocaleLowerCase('tr-TR'));
      });
    });
    const printerMovementItemIds = new Set(
      stockMovements
        .filter(movement => movement.ticketId && allPrinterTicketIds.has(movement.ticketId))
        .map(movement => movement.itemId)
    );
    const printerItems = inventory.filter(item => {
      if (item.deviceArea) return item.deviceArea === 'printer';
      return printerTicketPartIds.has(item.id) ||
        printerMovementItemIds.has(item.id) ||
        printerPartNames.has(item.name.trim().toLocaleLowerCase('tr-TR')) ||
        Boolean(item.code && printerPartCodes.has(item.code.trim().toLocaleLowerCase('tr-TR'))) ||
        isPrinterInventoryItem(item);
    });
    const [selectedYear, selectedMonth] = month.split('-').map(Number);
    const previousMonthDate = new Date(selectedYear, selectedMonth - 2, 1);
    const previousMonthStart = `${previousMonthDate.getFullYear()}-${String(previousMonthDate.getMonth() + 1).padStart(2, '0')}-01`; // Önceki ayın ilk günü açılış stoku
    const stockTotals = printerItems.reduce((sum, item) => {
      const movementsSinceOpening = stockMovements
        .filter(movement => movement.itemId === item.id && movement.date.substring(0, 10) >= previousMonthStart)
        .reduce((movementSum, movement) => movementSum + ((movement.newQuantity ?? movement.previousQuantity) - movement.previousQuantity), 0);
      const currentQuantity = Math.max(0, item.quantity || 0);
      const createdDuringOrAfterOpening = Boolean(item.createdAt && item.createdAt.substring(0, 10) >= previousMonthStart);
      const previousQuantity = createdDuringOrAfterOpening ? 0 : Math.max(0, currentQuantity - movementsSinceOpening);
      const unitCost = calculateItemWeightedAvgCost(item);
      return {
        currentQuantity: sum.currentQuantity + currentQuantity,
        previousQuantity: sum.previousQuantity + previousQuantity,
        currentValue: sum.currentValue + currentQuantity * unitCost,
        previousValue: sum.previousValue + previousQuantity * unitCost,
      };
    }, { currentQuantity: 0, previousQuantity: 0, currentValue: 0, previousValue: 0 });

    const settlement = Number(companyInfo.printerMonthlySettlements?.[month] || 0);
    const netRevenue = guaranteedExVat + nonGuaranteedExVat + outsideServiceRevenue + settlement - usedPartsNet - outsideServiceExpense;
    return {
      previousStock: stockTotals.previousQuantity,
      currentStock: stockTotals.currentQuantity,
      previousStockValue: stockTotals.previousValue,
      currentStockValue: stockTotals.currentValue,
      usedPartsNet, guaranteedExVat, nonGuaranteedExVat, outsideServiceRevenue, settlement, outsideServiceExpense, netRevenue,
      collectedInMonth, toBeCollectedFromMonthForms, ticketCount: printerPaymentTickets.length,
    };
  }, [tickets, inventory, stockMovements, companyInfo, month]);

  const saveSettlement = () => {
    if (!onSaveCompanyInfo) return;
    const amount = Math.max(0, Number(settlementDraft.replace(',', '.')) || 0);
    onSaveCompanyInfo({
      ...companyInfo,
      printerMonthlySettlements: { ...companyInfo.printerMonthlySettlements, [month]: amount },
    });
  };

  const cells = [
    { label: '1 Ay Önceki Stok', value: `${report.previousStock} adet · ${money(report.previousStockValue)}` },
    { label: 'Güncel Stok', value: `${report.currentStock} adet · ${money(report.currentStockValue)}` },
    { label: 'Kullanılan Net Parça', value: money(report.usedPartsNet) },
    { label: 'Garantili KDV’siz', value: money(report.guaranteedExVat) },
    { label: 'Garantisiz KDV’siz', value: money(report.nonGuaranteedExVat) },
    { label: 'Dış Servis Geliri (KDV’siz)', value: money(report.outsideServiceRevenue) },
    { label: 'Ay İçinde Alınan Tahsilat', value: money(report.collectedInMonth) },
    { label: 'Ayın Formlarından Alınacak', value: money(report.toBeCollectedFromMonthForms) },
    { label: 'Hakediş (KDV’siz)', value: money(report.settlement) },
    { label: 'Şehir Dışı Servis Gideri', value: money(report.outsideServiceExpense) },
    { label: 'Net Ciro', value: money(report.netRevenue), emphasis: true },
  ];

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <PackageCheck className="h-5 w-5 text-blue-600" /> Yazıcı Aylık Raporu
            </h3>
            <p className="mt-1 text-xs text-slate-500">{report.ticketCount} yazıcı/tarayıcı formu · gelirler {month} tahsilat tarihine göre</p>
          </div>
          <button
            type="button"
            onClick={() => exportPrinterMonthlyReportPDF(
              month,
              companyInfo.name || '',
              cells.map(cell => ({ label: cell.label, value: cell.value })),
              tickets.filter(ticket => !ticket.isDeleted && isPrinterTicket(ticket, companyInfo) && ticket.serviceType !== 'Sozlesmeli' && !ticket.contractedCompanyId)
            )}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-900 px-3.5 text-xs font-bold text-white hover:bg-slate-800"
          >
            <Download className="h-4 w-4 text-emerald-300" /> PDF İndir
          </button>
          {canEditSettlement && (
            <div className="flex flex-wrap items-end gap-2 rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-emerald-900">
                Ay sonunda merkezden alınan hakediş (KDV’siz, TL)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settlementDraft}
                  onChange={event => setSettlementDraft(event.target.value)}
                  className="h-9 w-48 rounded-lg border border-emerald-200 bg-white px-2.5 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-300"
                />
              </label>
              <button type="button" onClick={saveSettlement} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-700 px-3 text-xs font-bold text-white hover:bg-emerald-800">
                <Save className="h-3.5 w-3.5" /> Kaydet
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {cells.map(cell => (
            <div key={cell.label} className={`min-w-0 rounded-xl border p-3.5 ${cell.emphasis ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50/70'}`}>
              <p className="text-[11px] font-semibold text-slate-500">{cell.label}</p>
              <p className={`mt-1 break-words font-mono text-lg font-black ${cell.emphasis ? 'text-blue-800' : 'text-slate-900'}`}>{cell.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3.5 py-3 text-[11px] leading-relaxed text-slate-600">
          <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <p>Ay içinde alınan tahsilat ödeme gününe göre hesaplanır; ayın servis formlarındaki kalan tutar ayrıca alınacak alacak olarak gösterilir. Ödeme tarihi ve form numarası PDF detayında listelenir. Yapılan iş adedi, parça kullanımı ve şehir dışı giderleri servis işlem ayına göre hesaplanır. Gelir tutarları KDV hariç orantılı tahsilattır. Stokların TL değeri stok kartlarındaki güncel ağırlıklı ortalama birim maliyetle hesaplanır. Net ciro = garantili + garantisiz + dış servis geliri + hakediş − kullanılan parça maliyeti − şehir dışı servis giderleri.</p>
        </div>
      </section>
    </div>
  );
};
